import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import { awsImageRootUrl } from "lib/constants";
import { Category, fetchCategory } from "lib/features/category/categoryService";
import { useGetInfluencerTypeQuery } from "lib/features/influencer/influencerService";
import { InfluencerType } from "lib/features/influencer/types";
import { useGetRegionsQuery } from "lib/features/region/regionService";
import { Region } from "lib/features/region/types";
import { useUploadImageMutation } from "lib/features/s3/s3Service";
import { UploadImageRequest } from "lib/features/s3/types";
import { UpdateUserDto, User } from "lib/features/user/types";
import { useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import authServices, { UserCustomerType } from "services/auth-service";
import { editProfileStyles } from "styles/RefereeDirectory/editProfile.styles";
import { getCroppedImage } from "util/cropImage";
import { v4 as uuid } from "uuid";
import Label from "../NewItemForm/components/Label";
import PostScript from "../NewItemForm/components/PostScript";
import editProfileValidation from "./EditProfile.schema";

type EditProfileForm = {
  data: User;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  fetchUser: () => Promise<void>;
};

const EditProfileForm = ({
  data,
  setIsEditing,
  fetchUser,
}: EditProfileForm) => {
  const theme = useTheme();
  const { data: regionsData } = useGetRegionsQuery(null, {});
  const { data: influencerTypesData } = useGetInfluencerTypeQuery(null, {});
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [uploadImage] = useUploadImageMutation();
  const [imageLoader, setImageLoader] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [customerTypesList, setCustomerTypeList] = useState<UserCustomerType[]>(
    []
  );

  const [openCropper, setOpenCropper] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

  const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const [formValues, setFormValues] = useState<UpdateUserDto>({
    email: data?.email ?? "",
    avatar: data?.avatar ?? "",
    role_id: data?.role_id ?? 0,
    stripe_id: data?.stripe_id ?? "",
    influencer_type_id: data?.influencer_type?.id ?? 1,
    work_place: data?.work_place ?? "",
    first_name: data?.first_name ?? "",
    last_name: data?.last_name ?? "",
    about_me: data?.about_me ?? "",
    region_id: data?.regions?.[0]?.region?.id ?? 0,
    customer_type_ids: data?.customer_types?.map((c) => c.id) || [],
    categories_ids: data?.categories?.map((c) => c.id) || [],
    job_type: data?.job_type ?? "",
  });

  const resizeAndUploadImage = async (image: string) => {
    return new Promise((resolve, reject) => {
      const id = uuid() + ".jpg";
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");

      if (ctx && canvas) {
        const img = new Image();
        img.onload = async () => {
          const targetWidth = 1080;
          const scaleFactor = targetWidth / img.width;
          const targetHeight = img.height * scaleFactor;

          canvas.width = targetWidth;
          canvas.height = targetHeight;

          ctx.clearRect(0, 0, targetWidth, targetHeight);
          ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

          // Convert canvas image to base64
          const base64String = canvas.toDataURL("image/jpeg").split(",")[1];
          const fileSize = atob(base64String).length;

          const payload: UploadImageRequest = {
            id,
            image: {
              base64: base64String,
              width: targetWidth,
              height: targetHeight,
              fileSize,
              uri: "",
              type: "image/jpeg",
              fileName: id,
            },
          };

          try {
            setImageLoader(true);
            const result = await uploadImage(payload);
            resolve(result?.data);
          } catch (error) {
            console.log("Upload error:", error);
            reject(error);
          } finally {
            setImageLoader(false);
          }
        };

        img.onerror = (err) => reject("Error loading image.");
        img.src = image;
      } else {
        reject("Canvas context is not available.");
      }
    });
  };

  const onSubmit = async (values: UpdateUserDto) => {
    if (!data?.id) return;
    try {
      setLoader(true);
      let payload: UpdateUserDto = {
        email: values?.email ?? "",
        avatar: values?.avatar ?? "",
        role_id: values?.role_id ?? 0,
        stripe_id: values?.stripe_id ?? "",
        influencer_type_id: values?.influencer_type_id,
        work_place: values?.work_place,
        first_name: values?.first_name ?? "",
        last_name: values?.last_name ?? "",
        about_me: values?.about_me ?? "",
        region_id: values?.region_id ?? 0,
        customer_type_ids: values?.customer_type_ids ?? [],
        categories_ids: values?.categories_ids ?? [],
        job_type: values.influencer_type_id === 10 ? values.job_type || "" : "",
      };
      await authServices.updateUser(payload, data.id);
      setIsEditing(false);
      fetchUser();
    } catch (error) {
      console.log("Error updating profile:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    const getCustomerType = async () => {
      authServices.getCustomerType().then((response) => {
        const customerTypes: UserCustomerType[] = response.data;
        setCustomerTypeList(customerTypes);
      });
    };
    getCustomerType();
  }, []);

  useEffect(() => {
    const getCategories = async () => {
      try {
        const categories = await fetchCategory([1]);
        setCategories(categories);
      } catch (error) {
        console.error("Error fetching categories", error);
      }
    };

    getCategories();
  }, []);

  const handleCropConfirm = async (setFieldValue: any) => {
    if (!imageSrc || !croppedAreaPixels) return;

    try {
      const croppedBase64 = await getCroppedImage(imageSrc, croppedAreaPixels);
      if (croppedBase64) {
        const response = await resizeAndUploadImage(croppedBase64);
        if (response) {
          const imageUrl = `${response}`;
          setFieldValue("avatar", imageUrl);
          setFormValues((prevValues: any) => ({
            ...prevValues,
            avatar: imageUrl,
          }));
        }
      }
    } catch (err) {
      console.error("Crop/Upload failed:", err);
    } finally {
      setOpenCropper(false);
    }
  };

  return (
    <Box sx={editProfileStyles.container}>
      <Formik
        initialValues={formValues}
        validationSchema={editProfileValidation}
        onSubmit={(values) => onSubmit(values)}
      >
        {({ values, errors, touched, setFieldValue, isValid }) => {
          const handleChange = (e: React.ChangeEvent<any>) => {
            const { name, value } = e.target;
            setFieldValue(name, value);
            setFormValues((prevValues: any) => ({
              ...prevValues,
              [name]: value,
            }));
          };

          const handleImageUpload = (
            event: React.ChangeEvent<HTMLInputElement>
          ) => {
            const file = event.target.files?.[0];
            if (file && file.type.startsWith("image/")) {
              const reader = new FileReader();
              reader.onload = (e: ProgressEvent<FileReader>) => {
                if (e.target?.result) {
                  setImageSrc(e.target.result as string); // store raw image
                  setOpenCropper(true); // open crop modal
                }
              };
              reader.readAsDataURL(file);
            }
          };

          const handleCustomerType = (customer_id: number) => {
            const businessTypes = values?.customer_type_ids;
            const updatedCustomerTypes = businessTypes?.includes(customer_id)
              ? businessTypes.filter((item) => item !== customer_id)
              : [...businessTypes, customer_id];

            setFieldValue("customer_type_ids", updatedCustomerTypes);
          };

          const handleCategory = (category_id: number) => {
            const categories_ids = values.categories_ids || [];
            const updatedCategories = categories_ids?.includes(category_id)
              ? categories_ids.filter((item) => item !== category_id)
              : [...categories_ids, category_id];

            setFieldValue("categories_ids", updatedCategories);
          };

          return (
            <Form>
              <Box sx={{ fontFamily: theme.typography.fontFamily }}>
                {/* Avatar */}
                <Box sx={editProfileStyles.avatarContainer}>
                  <img
                    src={`${awsImageRootUrl}/${values?.avatar}`}
                    alt="Profile Avatar"
                    style={{
                      width: "200px",
                      height: "200px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      margin: "0 auto",
                    }}
                  />
                  <canvas
                    ref={canvasRef}
                    width={500}
                    height={500}
                    style={{ display: "none" }}
                  ></canvas>
                  <label htmlFor="upload-image">
                    <Typography sx={editProfileStyles.updateAvatarText}>
                      Update Profile Picture
                    </Typography>
                  </label>
                  <input
                    type="file"
                    id="upload-image"
                    onChange={handleImageUpload}
                    style={{ display: "none" }}
                    accept="image/*"
                  />

                  <Button
                    sx={editProfileStyles.backButton}
                    onClick={() => setIsEditing(false)}
                  >
                    Back
                  </Button>
                </Box>

                {/* First Name */}
                <Box>
                  <Label label="First Name" required={true} />
                  <Field
                    as={TextField}
                    name="first_name"
                    fullWidth
                    onChange={handleChange}
                    error={touched.first_name && Boolean(errors.first_name)}
                  />
                  {errors.first_name && touched.first_name && (
                    <Typography color="red" fontSize="12px">
                      {errors.first_name}
                    </Typography>
                  )}
                </Box>

                {/* Last Name */}
                <Box>
                  <Label label="Last Name" required={true} />
                  <Field
                    as={TextField}
                    name="last_name"
                    fullWidth
                    onChange={handleChange}
                    error={touched.last_name && Boolean(errors.last_name)}
                  />
                  {errors.last_name && touched.last_name && (
                    <Typography color="red" fontSize="12px">
                      {errors.last_name}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Label label="Job Type" required={true} />
                  <FormControl fullWidth>
                    <Select
                      name="influencer"
                      value={values.influencer_type_id || ""}
                      onChange={(e) => {
                        const selectedRegionId = e.target.value;
                        setFieldValue("influencer_type_id", selectedRegionId);
                      }}
                    >
                      {influencerTypesData?.map(
                        (influencerType: InfluencerType) => (
                          <MenuItem
                            key={influencerType.id}
                            value={influencerType.id}
                          >
                            {influencerType.type}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Box>

                {Number(values?.influencer_type_id) === 10 && (
                  <Box>
                    <Label label="Specify Job Type" required={false} />
                    <Field
                      as={TextField}
                      name="job_type"
                      fullWidth
                      onChange={handleChange}
                      error={touched.job_type && Boolean(errors.job_type)}
                    />
                    {errors.job_type && touched.job_type && (
                      <Typography color="red" fontSize="12px">
                        {errors.job_type}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Company Name */}
                <Box>
                  <Label label="Company Name" required={false} />
                  <Field
                    as={TextField}
                    name="work_place"
                    fullWidth
                    onChange={handleChange}
                    error={touched.work_place && Boolean(errors.work_place)}
                  />
                  <PostScript
                    text="Allows businesses to learn more about their referral partners."
                    italics='Ex: "One Hour Guided Jet Ski Tour"'
                  />
                </Box>

                <Box>
                  <Label label="City" required={false} />

                  <FormControl fullWidth>
                    <Select
                      variant="outlined"
                      name="region_id"
                      value={values.region_id || ""}
                      onChange={(e) => {
                        const selectedRegionId = e.target.value;
                        setFieldValue("region_id", selectedRegionId);
                      }}
                    >
                      {regionsData?.map((region: Region) => (
                        <MenuItem key={region.id} value={region.id}>
                          {region.region}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* About Me */}
                <Box>
                  <Label label="About Me" required={true} />
                  <Field
                    as={TextField}
                    name="about_me"
                    fullWidth
                    onChange={handleChange}
                    multiline
                    rows={4}
                    error={touched.about_me && Boolean(errors.about_me)}
                  />
                  {errors.about_me && touched.about_me && (
                    <Typography color="red" fontSize="12px">
                      {errors.about_me}
                    </Typography>
                  )}
                </Box>

                {/* Customer Type */}
                <Box>
                  <Label
                    label="Select your top 3 most common customer type: "
                    required={true}
                  />
                  <Box display="flex" flexWrap="wrap" mt={1}>
                    {customerTypesList &&
                      customerTypesList?.map((customer: UserCustomerType) => {
                        const _selected = values?.customer_type_ids?.includes(
                          customer?.id
                        );

                        return (
                          <Chip
                            key={customer.id}
                            label={customer.type}
                            onClick={() => {
                              handleCustomerType(customer.id);
                            }}
                            variant={_selected ? "filled" : "outlined"}
                            color={_selected ? "primary" : "default"}
                            sx={{
                              margin: "0 0.5rem 0.5rem 0",
                              border: _selected ? "none" : "1px solid #000",
                            }}
                          />
                        );
                      })}
                  </Box>
                  {errors.customer_type_ids && touched.customer_type_ids && (
                    <Typography color="red" fontSize="12px">
                      {errors.customer_type_ids}
                    </Typography>
                  )}
                </Box>

                {/* Categories */}
                <Box>
                  <Label
                    label="What are you most interesting on recommending?"
                    required={true}
                  />
                  <Box display="flex" flexWrap="wrap" mt={1}>
                    {categories &&
                      categories?.map((category: Category) => {
                        const _selected = values?.categories_ids?.includes(
                          category?.id
                        );

                        return (
                          <Chip
                            key={category.id}
                            label={category.category}
                            onClick={() => {
                              handleCategory(category.id);
                            }}
                            variant={_selected ? "filled" : "outlined"}
                            color={_selected ? "primary" : "default"}
                            sx={{
                              margin: "0 0.5rem 0.5rem 0",
                              border: _selected ? "none" : "1px solid #000",
                            }}
                          />
                        );
                      })}
                  </Box>
                  {errors.categories_ids && touched.categories_ids && (
                    <Typography color="red" fontSize="12px">
                      {errors.categories_ids}
                    </Typography>
                  )}
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={imageLoader || loader || !isValid}
                  sx={{ width: "100%", marginTop: "20px", paddingY: "10px" }}
                >
                  Save
                </Button>
              </Box>
              <Dialog
                open={openCropper}
                onClose={() => setOpenCropper(false)}
                maxWidth="sm"
                fullWidth
              >
                <DialogContent
                  sx={{ position: "relative", height: 400, background: "#333" }}
                >
                  {imageSrc && (
                    <Cropper
                      image={imageSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}   // change here to adjust the aspect ratio
                      cropShape="round"
                      rotation={0}
                      showGrid={true}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setOpenCropper(false)}>Cancel</Button>
                  <Button
                    variant="contained"
                    onClick={() => handleCropConfirm(setFieldValue)}
                  >
                    Crop & Upload
                  </Button>
                </DialogActions>
              </Dialog>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default EditProfileForm;
